import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Plus, Edit, Trash2, Eye, History, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/custom/Dialog';
import { httpClient } from '../../services/httpClient';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import { type FormTemplate } from '../../services/formTemplateService';

// ConfirmDialog component for delete confirmations
interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
}

const ConfirmDialog = ({ isOpen, onClose, onConfirm, title, message }: ConfirmDialogProps) => {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
        </div>
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600"
          >
            Delete
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// httpClient automatically handles authentication and tenant ID
import FormTemplateBuilder from '../forms/FormTemplateBuilder';
import DynamicFormRenderer from '../forms/DynamicFormRenderer';

// Interface for the form template structure
// Using FormTemplate from formTemplateService
// Additional properties for template management
interface ExtendedFormTemplate {
  id: number; // Required for existing templates
  name: string;
  description: string; // Make description required
  schema: any; // Schema can be any structure
  created_at: string; // Required for existing templates
  updated_at: string; // Required for existing templates
  version?: number; // Add version property
  is_latest?: boolean;
  parent_template_id?: number; // ID of the original template if this is a version
}

// Interface for version history item
interface TemplateVersion {
  id: number;
  template_id: number;
  version: number;
  name: string;
  description: string;
  schema: any[];
  created_at: string;
  is_active: boolean;
}

const FormTemplateManagement: React.FC = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<ExtendedFormTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [editingTemplate, setEditingTemplate] = useState<ExtendedFormTemplate | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [versionHistoryModalOpen, setVersionHistoryModalOpen] = useState<boolean>(false);
  const [currentTemplate, setCurrentTemplate] = useState<ExtendedFormTemplate | null>(null);
  const [templateVersions, setTemplateVersions] = useState<TemplateVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'form' | 'schema'>('form');

  // States for confirmation dialogs
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [templateToDelete, setTemplateToDelete] = useState<number | null>(null);

  // Toast notification helper function
  const { showToast } = useToast();

  // Fetch all form templates when component mounts
  useEffect(() => {
    fetchFormTemplates();
  }, []);

  // Function to fetch all form templates
  const fetchFormTemplates = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await httpClient.get<FormTemplate[]>('/api/form-templates');

      // Convert FormTemplate[] to ExtendedFormTemplate[]
      const extendedTemplates = response.map((template) => ({
        ...template,
        description: template.description || '' // Ensure description is not undefined
      })) as ExtendedFormTemplate[];

      setTemplates(extendedTemplates);
    } catch (error: any) {
      console.error('Error fetching form templates:', error);
      setError('Failed to fetch form templates');
      showToast('Failed to fetch form templates: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Function to confirm template deletion
  const confirmDeleteTemplate = (id: number) => {
    setTemplateToDelete(id);
    setDeleteConfirmOpen(true);
  };

  // Function to delete a form template
  const deleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      setError('');

      await httpClient.delete(`/api/form-templates/${templateToDelete}`);

      showToast('Form template deleted successfully', 'success');
      fetchFormTemplates(); // Refresh the list
    } catch (error: any) {
      console.error('Error deleting form template:', error);
      setError('Failed to delete form template');
      showToast('Failed to delete form template: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setTemplateToDelete(null);
    }
  };

  // Function to handle form template creation
  const handleCreateTemplate = async (template: any) => {
    try {
      setError('');

      await httpClient.post('/api/form-templates', template);
      // The FormTemplateBuilder component already makes the API call
      // So we just need to handle the success case here
      setCreateModalOpen(false);
      fetchFormTemplates(); // Refresh the list
    } catch (error: any) {
      console.error('Error creating form template:', error);
      setError('Failed to create form template');
      showToast('Failed to create form template: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  // Function to handle form template update
  const handleUpdateTemplate = async (template: any) => {
    if (!editingTemplate) return;

    try {
      setError('');

      await httpClient.put(`/api/form-templates/${editingTemplate.id}`, {
        ...template,
        create_new_version: true
      });

      showToast('Template updated successfully', 'success');
      setEditModalOpen(false);
      setEditingTemplate(null);
      fetchFormTemplates(); // Refresh the list
    } catch (error: any) {
      console.error('Error updating form template:', error);
      setError('Failed to update form template');
      showToast('Failed to update form template: ' + (error.message || 'Unknown error'), 'error');
    }
  };

  // Function to fetch template version history
  const fetchTemplateVersions = async (templateId: number) => {
    try {
      setVersionsLoading(true);
      setError('');

      const response = await httpClient.get<TemplateVersion[]>(
        `/api/form-templates/${templateId}/versions`
      );

      setTemplateVersions(response);
    } catch (error: any) {
      console.error('Error fetching template versions:', error);
      setError('Failed to fetch template versions');
      showToast(
        'Failed to fetch template versions: ' + (error.message || 'Unknown error'),
        'error'
      );
    } finally {
      setVersionsLoading(false);
    }
  };

  // Function to restore a previous template version
  const restoreTemplateVersion = async (versionId: number) => {
    if (!currentTemplate?.id) return;

    try {
      setVersionsLoading(true);
      setError('');

      await httpClient.post(
        `/api/form-templates/${currentTemplate.id}/restore-version/${versionId}`
      );

      showToast('Template version restored successfully', 'success');
      setVersionHistoryModalOpen(false);
      setCurrentTemplate(null);
      fetchFormTemplates(); // Refresh the list
    } catch (error: any) {
      console.error('Error restoring template version:', error);
      setError('Failed to restore template version');
      showToast(
        'Failed to restore template version: ' + (error.message || 'Unknown error'),
        'error'
      );
    } finally {
      setVersionsLoading(false);
    }
  };

  return (
    <div>
      {/* Delete confirmation dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={deleteTemplate}
        title="Delete Form Template"
        message="Are you sure you want to delete this form template? This action cannot be undone."
      />

      {/* Toast notifications */}
      {error && (
        <div className={`p-4 rounded-md mb-6 ${theme === 'dark' ? 'bg-red-900/30' : 'bg-red-50'}`}>
          <p className={`text-sm ${theme === 'dark' ? 'text-red-200' : 'text-red-700'}`}>{error}</p>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <div>
          <h2
            className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
          >
            {t('settings.templateManagement.title', 'Templates')}
          </h2>
          <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
            {t(
              'settings.templateManagement.description',
              'Create and manage templates for tickets'
            )}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setCreateModalOpen(true)}
            className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
              theme === 'dark'
                ? 'bg-indigo-600 hover:bg-indigo-500'
                : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            <Plus className="-ml-1 mr-2 h-5 w-5" />
            {t('metadata.addNew')}
          </button>
        </div>
      </div>

      <div className="mt-4">
        <div className="overflow-x-auto">
          <table
            className={`min-w-full divide-y ${
              theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
            }`}
          >
            <thead className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
              <tr>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  Name
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  Description
                </th>
                <th
                  className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  Created At
                </th>
                <th
                  className={`relative px-6 py-3 text-xs font-medium uppercase tracking-wider ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody
              className={
                theme === 'dark'
                  ? 'bg-gray-800 divide-y divide-gray-700'
                  : 'bg-white divide-y divide-gray-200'
              }
            >
              {templates.map((template) => (
                <tr key={template.id}>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm ${
                      theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                    }`}
                  >
                    {template.name}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}
                  >
                    {template.description || '-'}
                  </td>
                  <td
                    className={`px-6 py-4 whitespace-nowrap text-sm ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                    }`}
                  >
                    {template.created_at
                      ? new Date(template.created_at).toLocaleString('en-US', {
                          timeZone: 'Asia/Yangon', // Myanmar timezone (UTC+06:30)
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    <button
                      onClick={() => {
                        // Ensure template has all required properties
                        const previewTemplate: ExtendedFormTemplate = {
                          id: template.id,
                          name: template.name,
                          description: template.description,
                          schema: template.schema,
                          created_at: template.created_at,
                          updated_at: template.updated_at,
                          version: template.version,
                          is_latest: template.is_latest,
                          parent_template_id: template.parent_template_id
                        };
                        setCurrentTemplate(previewTemplate);
                        setPreviewModalOpen(true);
                      }}
                      className={
                        theme === 'dark'
                          ? 'text-indigo-400 hover:text-indigo-300'
                          : 'text-indigo-600 hover:text-indigo-900'
                      }
                      title="Preview template"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        // Set the current template data and open edit modal
                        const editTemplate: ExtendedFormTemplate = {
                          id: template.id,
                          name: template.name,
                          description: template.description,
                          schema: Array.isArray(template.schema) ? template.schema : [],
                          created_at: template.created_at,
                          updated_at: template.updated_at,
                          version: template.version,
                          is_latest: template.is_latest,
                          parent_template_id: template.parent_template_id
                        };
                        setEditingTemplate(editTemplate);
                        setEditModalOpen(true);
                      }}
                      className={
                        theme === 'dark'
                          ? 'text-indigo-400 hover:text-indigo-300'
                          : 'text-indigo-600 hover:text-indigo-900'
                      }
                      title="Edit template"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => {
                        // Ensure template has all required properties
                        const historyTemplate: ExtendedFormTemplate = {
                          id: template.id,
                          name: template.name,
                          description: template.description,
                          schema: template.schema,
                          created_at: template.created_at,
                          updated_at: template.updated_at,
                          version: template.version,
                          is_latest: template.is_latest,
                          parent_template_id: template.parent_template_id
                        };
                        setCurrentTemplate(historyTemplate);
                        fetchTemplateVersions(template.id);
                        setVersionHistoryModalOpen(true);
                      }}
                      className={
                        theme === 'dark'
                          ? 'text-blue-400 hover:text-blue-300'
                          : 'text-blue-600 hover:text-blue-900'
                      }
                      title="Version history"
                    >
                      <History className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => template.id && confirmDeleteTemplate(template.id)}
                      className={
                        theme === 'dark'
                          ? 'text-red-400 hover:text-red-300'
                          : 'text-red-600 hover:text-red-900'
                      }
                      title="Delete template"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
              {templates.length === 0 && !loading && (
                <tr>
                  <td
                    colSpan={4}
                    className={`px-6 py-4 text-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    No templates found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {loading && (
            <div className="flex justify-center py-4">
              <div
                className={`animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 ${theme === 'dark' ? 'border-indigo-400' : 'border-indigo-600'}`}
              ></div>
            </div>
          )}
        </div>
      </div>

      {/* Create Form Template Dialog */}
      <Dialog
        open={createModalOpen}
        onOpenChange={(open) => {
          if (!open) setCreateModalOpen(false);
        }}
      >
        <DialogContent className="sm:max-w-4xl md:max-w-screen-lg lg:max-w-screen-2xl h-[70vh] lg:h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Create Template</DialogTitle>
          </DialogHeader>
          <div className="py-4 overflow-y-auto flex-1">
            <FormTemplateBuilder onSave={handleCreateTemplate} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Form Template Dialog */}
      <Dialog
        open={editModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setEditModalOpen(false);
            setEditingTemplate(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-4xl md:max-w-screen-lg lg:max-w-screen-2xl h-[70vh] lg:h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit Template: {editingTemplate?.name || ''}</DialogTitle>
          </DialogHeader>
          <div className="py-4 overflow-y-auto flex-1">
            {editingTemplate && (
              <FormTemplateBuilder
                initialTemplate={{
                  ...editingTemplate,
                  schema: Array.isArray(editingTemplate.schema) ? editingTemplate.schema : []
                }}
                onSave={handleUpdateTemplate}
                editMode={true}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Version History Dialog */}
      <Dialog
        open={versionHistoryModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setVersionHistoryModalOpen(false);
            setCurrentTemplate(null);
            setTemplateVersions([]);
          }
        }}
      >
        <DialogContent className="sm:max-w-4xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Version History: {currentTemplate?.name || ''}</DialogTitle>
          </DialogHeader>
          <div className="mb-4 overflow-y-auto flex-1">
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
              View and restore previous versions of this template. Note: Restoring a previous
              version will create a new version based on the selected one.
            </p>
          </div>

          {versionsLoading ? (
            <div className="flex justify-center py-4">
              <div
                className={`animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 ${theme === 'dark' ? 'border-indigo-400' : 'border-indigo-600'}`}
              ></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table
                className={`min-w-full divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}
              >
                <thead className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
                  <tr>
                    <th
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      Version
                    </th>
                    <th
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      Created At
                    </th>
                    <th
                      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      Status
                    </th>
                    <th
                      className={`relative px-6 py-3 text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={
                    theme === 'dark'
                      ? 'bg-gray-800 divide-y divide-gray-700'
                      : 'bg-white divide-y divide-gray-200'
                  }
                >
                  {templateVersions.map((version) => (
                    <tr key={version.id}>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${
                          theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                        }`}
                      >
                        {version.version}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                        }`}
                      >
                        {version.created_at
                          ? new Date(version.created_at).toLocaleString('en-US', {
                              timeZone: 'Asia/Yangon', // Myanmar timezone (UTC+06:30)
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : '-'}
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                        }`}
                      >
                        {version.is_active ? (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${theme === 'dark' ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'}`}
                          >
                            Current
                          </span>
                        ) : (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`}
                          >
                            Historical
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {!version.is_active && (
                          <button
                            onClick={() => restoreTemplateVersion(version.id)}
                            className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white ${theme === 'dark' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                          >
                            Restore
                          </button>
                        )}
                        <button
                          onClick={() => {
                            // Preview this version
                            if (currentTemplate) {
                              const templateVersion: ExtendedFormTemplate = {
                                id: currentTemplate.id,
                                name: version.name,
                                description: version.description,
                                schema: version.schema,
                                created_at: currentTemplate.created_at,
                                updated_at: currentTemplate.updated_at,
                                version: version.version
                              };
                              setCurrentTemplate(templateVersion);
                              setVersionHistoryModalOpen(false);
                              setPreviewModalOpen(true);
                            }
                          }}
                          className={`ml-2 inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded ${theme === 'dark' ? 'text-indigo-400 border-indigo-400 hover:bg-indigo-900/20' : 'text-indigo-600 border-indigo-600 hover:bg-indigo-50'}`}
                        >
                          Preview
                        </button>
                      </td>
                    </tr>
                  ))}
                  {templateVersions.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className={`px-6 py-4 text-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                      >
                        No version history available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Form Template Dialog */}
      <Dialog
        open={previewModalOpen}
        onOpenChange={(open) => {
          if (!open) setPreviewModalOpen(false);
        }}
      >
        <DialogContent className="sm:max-w-4xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Preview: {currentTemplate?.name || ''}{' '}
              {currentTemplate?.version ? `(v${currentTemplate.version})` : ''}
            </DialogTitle>
          </DialogHeader>

          {currentTemplate && (
            <div className="py-4 overflow-y-auto flex-1">
              <div className="flex flex-col space-y-4">
                <div className="border-b border-gray-200 dark:border-gray-700">
                  <nav className="-mb-px flex space-x-6">
                    <button
                      onClick={() => setActiveTab('form')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'form'
                          ? theme === 'dark'
                            ? 'border-indigo-400 text-indigo-400'
                            : 'border-indigo-500 text-indigo-600'
                          : theme === 'dark'
                            ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Form Preview
                    </button>
                    <button
                      onClick={() => setActiveTab('schema')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'schema'
                          ? theme === 'dark'
                            ? 'border-indigo-400 text-indigo-400'
                            : 'border-indigo-500 text-indigo-600'
                          : theme === 'dark'
                            ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Schema JSON
                    </button>
                  </nav>
                </div>

                <div className="pt-2">
                  {activeTab === 'form' && currentTemplate && (
                    <DynamicFormRenderer
                      formTemplate={{
                        ...currentTemplate,
                        id: currentTemplate.id || 0, // Ensure id is not undefined
                        created_at: currentTemplate.created_at || new Date().toISOString(), // Ensure created_at is not undefined
                        updated_at: currentTemplate.updated_at || new Date().toISOString() // Ensure updated_at is not undefined
                      }}
                      onSubmit={(values) => console.log('Form values:', values)}
                      readOnly={true}
                    />
                  )}
                  {activeTab === 'schema' && (
                    <pre
                      className={`p-4 rounded-md ${theme === 'dark' ? 'bg-gray-900 text-gray-300' : 'bg-gray-50 text-gray-800'}`}
                      style={{ maxHeight: '400px', overflow: 'auto' }}
                    >
                      {JSON.stringify(currentTemplate.schema, null, 2)}
                    </pre>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setPreviewModalOpen(false)}
                  className={`px-4 py-2 text-sm font-medium rounded-md ${
                    theme === 'dark'
                      ? 'text-gray-300 hover:text-gray-200 bg-gray-700 hover:bg-gray-600'
                      : 'text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FormTemplateManagement;
